import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  MessageCircle, Phone, Star, Shield, MapPin, Clock, CheckCircle,
  Users, Award, Smile, Stethoscope, HeartPulse, IndianRupee, Instagram,
  ChevronLeft, ChevronRight, ArrowRight,
} from "lucide-react";
import WhatsAppIcon from "@/components/icons/WhatsAppIcon";
import { CATEGORIES } from "@/lib/seo-categories";
import { getSeoPage } from "@/lib/seo-content";
import { resolveAssetPath } from "@/lib/seo-image-loader";
import {
  getWhatsAppUrl, PHONE_PRIMARY, PHONE_PRIMARY_TEL, DOCTOR_NAME,
  CLINIC_TIMINGS, GOOGLE_MAPS_EMBED, CLINIC_NAME, SERVICES_NAV,
  INSTAGRAM, INSTAGRAM_URL, TREATMENT_OPTIONS, PREFERRED_TIME_OPTIONS,
  CONSENT_TEXT, PRIVACY_LINE, WHATSAPP_BOOKING_INTRO, GOOGLE_MAPS_DIRECTIONS, CLINIC_ADDRESS,
} from "@/lib/constants";
import { track, trackCall, trackWhatsApp, trackDirections, trackFormSubmit } from "@/lib/analytics";
import CTABanner from "@/components/CTABanner";
import InstagramEmbeds from "@/components/InstagramEmbeds";
import BeforeAfterCarousel from "@/components/BeforeAfterCarousel";
import doctorImg from "@/assets/doctor/doctor-hero-clinic.jpg";
import doctorPortrait from "@/assets/team/team-doctor-portrait.jpg";
import clinicImg from "@/assets/clinic/clinic-1.jpg";
import heroImg from "@/assets/doctor/doctor-hero-clinic.jpg";
import patient1 from "@/assets/patients/patient-1.jpg";
import patient2 from "@/assets/patients/patient-2.jpg";

const trustBadges = [
  { icon: Users, label: "10,000+", sublabel: "Happy Patients" },
  { icon: Award, label: "15+", sublabel: "Years Experience" },
  { icon: Shield, label: "25+", sublabel: "Treatments" },
  { icon: Star, label: "4.9★", sublabel: "Google Rating" },
];

const trustSignals = [
  { icon: Stethoscope, title: "Expert Doctor", desc: "Qualified & experienced dental specialists" },
  { icon: HeartPulse, title: "Painless Treatments", desc: "Advanced technology for comfortable care" },
  { icon: IndianRupee, title: "Affordable Pricing", desc: "Transparent pricing with no hidden costs" },
  { icon: MapPin, title: "Easily Accessible", desc: "Central Rambaug location, easy from all of Kalyan" },
];

const whyChoose = [
  "State-of-the-art dental equipment & sterilization",
  "Personalized treatment plans for every patient",
  "Painless procedures with advanced anesthesia",
  "Affordable pricing with EMI options available",
  "Warm, friendly clinic environment",
  "10+ years of clinical excellence",
];

const faqs = [
  { q: "What are your clinic timings?", a: `We are open Mon-Sat: ${CLINIC_TIMINGS.weekdays}, Sunday: ${CLINIC_TIMINGS.sunday}. Closed on ${CLINIC_TIMINGS.closed}.` },
  { q: "Do you accept dental insurance?", a: "Yes, we accept most major dental insurance plans. Please contact us to verify your specific plan." },
  { q: "Is there parking available?", a: "Yes, convenient parking is available near our clinic in Rambaug, Kalyan." },
  { q: "Do you offer EMI or payment plans?", a: "Yes, we offer flexible EMI options for major treatments. Ask our team for details." },
  { q: "How do I book an appointment?", a: "You can book via WhatsApp, call us directly, or use the online booking form on our website." },
];

const googleReviews = [
  { name: "Yash Sapkale", rating: 5, review: "I was very scared of dental procedures. Dr Khushbu spoke to me and I got really confident about her skills. The implant procedure was done in 45 mins with no pain whatsoever. Clinic is absolutely cool and clean with great music ambience. Certainly the best dental surgeon in Kalyan.", time: "6 months ago" },
  { name: "Sudhir More", rating: 5, review: "Surely the top dental clinic in Kalyan. Very spacious and clean with great caring staff. During root canal procedure not feel any pain. We have full faith in Dr. Khusbu. My root canal treatment and caps were perfect. All the latest technology is available. Highly recommended!", time: "4 years ago" },
  { name: "Kedar Mali", rating: 5, review: "Dr. Khusboo Mam thoroughly explained the situation and risks with my damaged tooth. Her vast experience was evident as she restored the complete part of my tooth. The clinic staff was polite and courteous. I highly recommend Mutha's dental clinic for professional and quality dental care.", time: "3 years ago" },
  { name: "Jitendra Singh", rating: 5, review: "Best clinic for Invisalign! Getting my teeth straightened in the lowest cost for aligner across Kalyan. Treatment is fast and effective. Great team of doctors and clean & professional clinic.", time: "2 months ago" },
  { name: "Kirti Kedar", rating: 5, review: "Went for braces, had a good experience. Dr. Khusboo is very knowledgeable. Staff is very good. Will recommend to family and friends.", time: "1 year ago" },
  { name: "Naufil Rohe", rating: 5, review: "This year was my best Ramzan in 14 years as I could enjoy my Iftaari! Dr Khusboo completed 6 root canals, 2 tooth removals, 4 caps and 2 implants in 21 sittings. No pain at all. Excellent hygiene and good doctor's attitude. Very much satisfied!", time: "6 years ago" },
  { name: "Meenal Patel", rating: 5, review: "Visited for root canal and tooth removal. Must say Dr. Khushbu is excellent at her work. No extra medicines or pain killers were required, my treatment went smooth. A perfect dentist every Kalyankar must have.", time: "4 years ago" },
  { name: "Atul Joshi", rating: 5, review: "Scaling and polishing was an excellent experience with Dr. Khushbu. She takes personal care of all her patients. It's a relief to find a dentist with integrity. The front desk staff makes you feel right at home immediately.", time: "4 years ago" },
  { name: "Shreyas Samel", rating: 5, review: "Dr. Khushboo's expertise can be identified within first few minutes. Explaining things in layman's language, not forcing unnecessary treatments and curing ailments in a professional manner is her hallmark. Overall experience was superb.", time: "2 years ago" },
  { name: "Himanshu Pradhan", rating: 5, review: "After much research I discovered the best dentist near me for implants in Kalyan.", time: "1 year ago" },
  { name: "Yashwant Kosulkar", rating: 5, review: "Very good service, staff and Doctor. Good guidance throughout the treatment. Got my complete fixed set of teeth on 7 implants. Treatment finished in three months.", time: "6 years ago" },
  { name: "Ritika Bhattacharya", rating: 5, review: "Dr. Khushbu Mutha is excellent at her job. Me and my mother have had a very good experience. Root canal, implants, aligners and fillings. She's very skilled and has made our teeth look as good as new and even stronger!", time: "5 years ago" },
  { name: "Raveena Nagdev", rating: 5, review: "Very good experience with Dr. Khushboo Mutha. She handles everything with proper care and makes sure the patient is comfortable. I have undergone implant, root canal and veneers for smile designing. Very painless treatment.", time: "2 years ago" },
  { name: "Janisar Mujawar", rating: 5, review: "Glad to have found a dentist of Dr. Khushboo Mutha's calibre. The procedures are carried out smoothly with detailed explanation of issues and great recommendations on maintaining oral hygiene. Advanced modern techniques, overall hygiene, and great support staff.", time: "3 years ago" },
  { name: "Kishor Swain", rating: 5, review: "Dr. Khushboo is extremely professional, combines expertise and a willingness to listen & discuss. She has taken great care of my mother's dental implant. It's rare to find a doctor that combines personal touches with outstanding quality of dental care. HIGHLY recommend.", time: "2 years ago" },
  { name: "Pushpak Kolhe", rating: 5, review: "Despite my tight timeline before traveling abroad, Dr. Khushbu Mutha and her excellent team efficiently handled the entire implant procedure. Their expertise and professionalism ensured a smooth experience. I highly recommend Mutha Dental Clinic for top-notch dental services.", time: "2 years ago" },
];

const GoogleLogo = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const Index = () => {
  const [formData, setFormData] = useState({ name: "", phone: "", service: "", time: "", message: "", consent: false });
  const [formError, setFormError] = useState("");
  const reviewsRef = useRef<HTMLDivElement>(null);
  const heroImageRef = useRef<HTMLDivElement>(null);
  const { scrollY } = useScroll();
  const heroParallaxY = useTransform(scrollY, [0, 600], [0, 80]);
  const clinicParallaxY = useTransform(scrollY, [200, 1400], [0, -60]);

  const handleQuickBook = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return setFormError("Please enter your name");
    if (!/^[6-9]\d{9}$/.test(formData.phone)) return setFormError("Enter a valid 10-digit mobile number");
    if (!formData.service) return setFormError("Please select the treatment you need");
    if (!formData.consent) return setFormError("Please tick the consent box so we can contact you");
    setFormError("");
    trackFormSubmit("home_quick_book", formData.service);
    const msg = `${WHATSAPP_BOOKING_INTRO} ${formData.service}.\nName: ${formData.name}\nMobile: ${formData.phone}${formData.time ? `\nPreferred time: ${formData.time}` : ""}${formData.message ? `\nMessage: ${formData.message}` : ""}\n(Source: Homepage)`;
    window.open(getWhatsAppUrl(msg), "_blank", "noopener,noreferrer");
  };

  const handlePhoneChange = (val: string) => {
    const digits = val.replace(/\D/g, "").slice(0, 10);
    setFormData({ ...formData, phone: digits });
  };

  const scrollReviews = (dir: "left" | "right") => {
    if (reviewsRef.current) {
      const scrollAmount = (reviewsRef.current.firstElementChild?.getBoundingClientRect().width ?? 300) + 16;
      reviewsRef.current.scrollBy({ left: dir === "left" ? -scrollAmount : scrollAmount, behavior: "smooth" });
    }
  };

  const bookingForm = (
    <Card className="min-w-0 max-w-full border-primary/20 bg-card/95 text-card-foreground shadow-soft-lg backdrop-blur">
      <CardContent className="p-5 sm:p-6">
        <h3 className="font-heading text-lg font-bold mb-0.5 text-center">Book Your Consultation</h3>
        <p className="text-xs text-muted-foreground text-center mb-4">Name and mobile number are all we need</p>
        <form onSubmit={handleQuickBook} className="space-y-3" noValidate>
          <Input placeholder="Full Name" autoComplete="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} maxLength={100} className="h-11" />
          <Input placeholder="Mobile Number (10 digits)" type="tel" inputMode="numeric" autoComplete="tel" pattern="[0-9]{10}" maxLength={10} value={formData.phone} onChange={(e) => handlePhoneChange(e.target.value)} className="h-11" />
          <Select onValueChange={(v) => setFormData({ ...formData, service: v })}>
            <SelectTrigger className="h-auto min-h-11 min-w-0 [&>span]:whitespace-normal text-left" aria-label="Treatment needed"><SelectValue placeholder="Treatment Needed" /></SelectTrigger>
            <SelectContent>{TREATMENT_OPTIONS.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent>
          </Select>
          <Select onValueChange={(v) => setFormData({ ...formData, time: v })}>
            <SelectTrigger className="h-auto min-h-11 min-w-0 [&>span]:whitespace-normal text-left" aria-label="Preferred time"><SelectValue placeholder="Preferred Time (optional)" /></SelectTrigger>
            <SelectContent>{PREFERRED_TIME_OPTIONS.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent>
          </Select>
          <Input placeholder="Tell us about your concern (optional)" value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} maxLength={300} className="h-11" />
          <label className="flex items-start gap-2 text-xs text-muted-foreground leading-relaxed">
            <input
              type="checkbox"
              checked={formData.consent}
              onChange={(e) => setFormData({ ...formData, consent: e.target.checked })}
              className="mt-0.5 h-4 w-4 accent-[hsl(var(--primary))]"
            />
            <span>{CONSENT_TEXT}</span>
          </label>
          {formError && <p className="text-destructive text-xs">{formError}</p>}
          <Button type="submit" className="w-full h-auto min-h-12 px-3 py-3 whitespace-normal tracking-normal" size="lg">
            Book My Consultation
          </Button>
          <p className="text-center text-[11px] text-muted-foreground">{PRIVACY_LINE}</p>
          <p className="text-center text-[11px] text-muted-foreground">
            Mon–Sat: {CLINIC_TIMINGS.weekdays} · Sunday: {CLINIC_TIMINGS.sunday}
          </p>
        </form>
      </CardContent>
    </Card>
  );


  return (
    <div>
      {/* Hero Section */}
      <section className="relative overflow-hidden" style={{ background: 'hsl(var(--background))' }}>
        {/* MOBILE HERO — stacked: image on top, text below */}
        <div className="lg:hidden">
          <div className="container pt-6 pb-3">
            {/* Headline */}
            <motion.h1
              className="mb-3 font-heading text-[2rem] sm:text-4xl font-bold leading-[1.15] tracking-tight"
              style={{ color: 'hsl(var(--primary))' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Trusted Dental Care in <span className="text-primary">Kalyan</span>
            </motion.h1>

            {/* Subtitle */}
            <p className="text-sm font-medium text-muted-foreground">
              Implants, Root Canals, Aligners and Complete Family Dentistry
            </p>
          </div>

          {/* Doctor image directly under the headline */}
          <div ref={heroImageRef} className="relative w-full aspect-[3/4] overflow-hidden" style={{ background: 'hsl(var(--background))' }}>
            <motion.img
              src={doctorImg}
              alt={`${DOCTOR_NAME} at ${CLINIC_NAME}`}
              className="w-full h-full object-cover object-top"
              width={800}
              height={1067}
              fetchPriority="high"
              decoding="async"
              style={{
                y: heroParallaxY,
                WebkitMaskImage:
                  'linear-gradient(to bottom, black 0%, black 55%, rgba(0,0,0,0.7) 75%, transparent 100%)',
                maskImage:
                  'linear-gradient(to bottom, black 0%, black 55%, rgba(0,0,0,0.7) 75%, transparent 100%)',
              }}
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, ease: 'easeOut' }}
            />
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3"
              style={{ background: 'linear-gradient(to bottom, transparent, hsl(var(--background)) 85%)' }}
            />
          </div>

          <div className="container pb-4">
            {/* Prominent Google reviews card */}
            <motion.a
              href="https://www.google.com/search?q=Mutha%27s+Advanced+Dental+Care+Kalyan#lrd=0x0:0x0,1"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("google_reviews_click", { source: "home_hero_mobile" })}
              className="mb-5 flex items-center gap-4 rounded-2xl border-2 border-primary/20 bg-card px-4 py-4 shadow-lg active:scale-[0.99]"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
            >
              <GoogleLogo />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="font-heading text-2xl font-bold leading-none text-foreground">4.9</span>
                  <div className="flex gap-0.5">
                    {[1,2,3,4,5].map(i => <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />)}
                  </div>
                </div>
                <p className="mt-1 text-xs font-medium text-muted-foreground">
                  350+ Google reviews · 10,000+ patients treated in Kalyan
                </p>
              </div>
              <ArrowRight className="h-4 w-4 shrink-0 text-primary" />
            </motion.a>

            {/* Description */}
            <p className="mb-5 text-sm leading-relaxed" style={{ color: 'hsl(var(--muted-foreground))' }}>
              Consult {DOCTOR_NAME} and the specialist team for clear treatment guidance, modern dental care and
              comfortable appointments — trusted by families across Kalyan.
            </p>

            {/* Primary CTA — Book */}
            <Link
              to="/book-appointment"
              onClick={() => track("book_appointment_click", { source: "home_hero_mobile" })}
              className="mb-3 flex w-full items-center justify-between rounded-2xl bg-primary px-5 py-4 text-primary-foreground shadow-md transition-all active:scale-[0.98]"
            >
              <span className="font-heading text-base font-semibold">Book My Consultation</span>
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20">
                <ArrowRight className="h-5 w-5 text-primary-foreground" />
              </span>
            </Link>

            {/* Secondary CTA — Call */}
            <a
              href={PHONE_PRIMARY_TEL}
              onClick={() => trackCall("home_hero_mobile")}
              className="mb-3 flex w-full items-center justify-between rounded-2xl border-2 border-primary/30 bg-card px-5 py-4 text-foreground shadow-sm transition-all active:scale-[0.98]"
            >
              <span className="font-heading text-base font-semibold">Call {PHONE_PRIMARY}</span>
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
                <Phone className="h-5 w-5 text-primary" />
              </span>
            </a>

            {/* Third CTA — WhatsApp */}
            <a
              href={getWhatsAppUrl(WHATSAPP_BOOKING_INTRO)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsApp("home_hero_mobile")}
              className="mb-3 flex w-full items-center justify-between rounded-2xl border-2 border-whatsapp/40 bg-card px-5 py-4 text-foreground shadow-sm transition-all active:scale-[0.98]"
            >
              <span className="font-heading text-base font-semibold">WhatsApp Us</span>
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-whatsapp/10">
                <WhatsAppIcon className="h-5 w-5 text-whatsapp" />
              </span>
            </a>

            {/* Hours near booking buttons */}
            <p className="text-xs text-muted-foreground">
              Mon–Sat: {CLINIC_TIMINGS.weekdays} · Sunday: {CLINIC_TIMINGS.sunday}
            </p>
          </div>
        </div>

        {/* DESKTOP HERO — side by side (unchanged) */}
        <div className="hidden lg:block">
          <div className="relative min-h-[580px]">
            <div className="absolute inset-0">
              <motion.img
                src={doctorImg}
                alt={`${DOCTOR_NAME} at ${CLINIC_NAME}`}
                className="absolute right-0 top-0 h-full w-[60%] ml-auto object-cover object-[center_15%]"
                width={1200}
                height={700}
                fetchPriority="high"
                decoding="async"
                style={{ y: heroParallaxY }}
              />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, hsl(var(--background)) 35%, hsla(0,0%,98%,0.85) 50%, transparent 70%)' }} />
              <div className="absolute inset-x-0 bottom-0 h-32" style={{ background: 'linear-gradient(to top, hsl(var(--background)), transparent)' }} />
            </div>
            <div className="container relative z-10 flex items-center min-h-[580px]">
              <div className="max-w-lg py-20">
                {/* Visual duplicate of the mobile H1 — kept as a div so the page has exactly one H1 */}
                <motion.div
                  className="mb-4 font-heading text-[3.5rem] font-bold leading-tight tracking-tight"
                  style={{ color: 'hsl(var(--primary))' }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  Trusted Dental Care in <span className="text-primary">Kalyan</span>
                </motion.div>
                <p className="mb-3 font-heading text-lg text-foreground/80">
                  Implants, Root Canals, Aligners and Complete Family Dentistry
                </p>
                <motion.p
                  className="mb-6 max-w-md text-base"
                  style={{ color: 'hsl(var(--muted-foreground))' }}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.35 }}
                >
                  Consult {DOCTOR_NAME} and the specialist team for clear treatment guidance, modern dental care and
                  comfortable appointments — trusted by families across Kalyan.
                </motion.p>
                <motion.div
                  className="mb-8 flex flex-wrap items-center gap-3"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                >
                  <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                    <Link to="/book-appointment" onClick={() => track("book_appointment_click", { source: "home_hero" })}>
                      Book My Consultation
                    </Link>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <a href={PHONE_PRIMARY_TEL} onClick={() => trackCall("home_hero")}>
                      <Phone className="mr-1.5 h-4 w-4" /> Call {PHONE_PRIMARY}
                    </a>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="border-whatsapp text-whatsapp hover:bg-whatsapp hover:text-white">
                    <a href={getWhatsAppUrl(WHATSAPP_BOOKING_INTRO)} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp("home_hero")}>
                      <MessageCircle className="mr-1.5 h-4 w-4" /> WhatsApp
                    </a>
                  </Button>
                </motion.div>
                <p className="mb-6 text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>
                  Mon–Sat: {CLINIC_TIMINGS.weekdays} · Sunday: {CLINIC_TIMINGS.sunday}
                </p>
                <motion.div
                  className="grid grid-cols-4 gap-3 max-w-lg"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.65 }}
                >
                  {trustBadges.map((badge, i) => (
                    <motion.div
                      key={badge.sublabel}
                      className="rounded-xl border border-border/50 p-2.5 text-center shadow-sm backdrop-blur-sm"
                      style={{ background: 'rgba(255,255,255,0.8)' }}
                      initial={{ opacity: 0, scale: 0.92 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: 0.75 + i * 0.08 }}
                    >
                      <badge.icon className="mx-auto mb-1 h-4 w-4 text-primary" />
                      <p className="font-heading text-base font-bold leading-none text-primary">{badge.label}</p>
                      <p className="mt-0.5 text-[10px] leading-tight" style={{ color: '#718096' }}>{badge.sublabel}</p>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Signals */}

      {/* Trust Signals */}
      <section className="py-12 bg-card border-b border-border">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {trustSignals.map((item, i) => (
              <motion.div
                key={item.title}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
              >
                <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-accent mb-3">
                  <item.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-sm mb-1">{item.title}</h3>
                <p className="text-xs text-muted-foreground">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold mb-3">Our Dental Services</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">Complete dental care — all under one roof in Kalyan</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CATEGORIES.map((cat, i) => {
              const Icon = cat.icon;
              const hub = getSeoPage(cat.hubSlug);
              const img = hub ? resolveAssetPath(hub.heroImage) : "";
              return (
                <motion.div
                  key={cat.key}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.45, delay: (i % 5) * 0.06, ease: "easeOut" }}
                >
                  <Link to={`/services/${cat.hubSlug}`} className="group block h-full">
                    <Card className="hover:shadow-xl hover:border-primary/30 hover:-translate-y-1 transition-all duration-300 h-full overflow-hidden">
                      {img && (
                        <div className="aspect-[16/10] overflow-hidden bg-muted">
                          <img
                            src={img}
                            alt={cat.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                      )}
                      <CardContent className="p-5">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="h-10 w-10 rounded-lg bg-accent flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                            <Icon className="h-5 w-5" />
                          </span>
                          <h3 className="font-heading font-semibold text-base">{cat.name}</h3>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{cat.blurb}</p>
                        <span className="text-xs font-medium text-primary">Explore {cat.shortName} →</span>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Book Appointment Section */}
      <section className="py-12 md:py-16 bg-primary/5 border-y border-border">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-8 items-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-3">Book Your Appointment</h2>
              <p className="text-muted-foreground mb-4 text-sm">
                Get started with a quick WhatsApp booking. Fill the form and we'll confirm your appointment instantly.
              </p>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> Mon-Sat: {CLINIC_TIMINGS.weekdays}</p>
                <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-primary" /> {PHONE_PRIMARY}</p>
                <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> Kalyan, Maharashtra</p>
              </div>
            </motion.div>
            <motion.div
              className="min-w-0"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              {bookingForm}
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-accent/30">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-6">Why Choose {CLINIC_NAME}?</h2>
              <ul className="space-y-3">
                {whyChoose.map((point, i) => (
                  <motion.li
                    key={point}
                    className="flex items-start gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: i * 0.07 }}
                  >
                    <CheckCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <span className="text-sm">{point}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              className="aspect-[4/3] md:aspect-[5/4] rounded-xl overflow-hidden shadow-lg border border-border bg-muted"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
            >
              <motion.img
                src={clinicImg}
                alt="Mutha's Advanced Dental Care clinic interior in Kalyan"
                className="w-full h-full object-cover"
                width={800}
                height={600}
                loading="lazy"
                decoding="async"
                style={{ y: clinicParallaxY, scale: 1.15 }}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Doctor Introduction */}
      <section className="py-16 md:py-20">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <motion.div
              className="aspect-[4/5] md:aspect-[3/4] rounded-xl overflow-hidden shadow-lg border border-border bg-muted order-2 md:order-1"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
            >
              <img src={doctorImg} alt={`${DOCTOR_NAME} - Dentist in Kalyan`} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700" width={600} height={800} loading="lazy" decoding="async" />
            </motion.div>
            <motion.div
              className="min-w-0 order-1 md:order-2"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">Meet {DOCTOR_NAME}</h2>
              <p className="text-muted-foreground mb-4">
                With over 10 years of clinical experience, {DOCTOR_NAME} is a leading dental specialist in Kalyan.
                She is passionate about creating beautiful smiles and ensuring every patient receives personalized,
                painless dental care.
              </p>
              <p className="text-muted-foreground mb-6">
                Specializing in dental implants, cosmetic dentistry, and full mouth rehabilitation,
                Dr. Mutha combines skill with compassion to deliver exceptional results.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button asChild>
                  <Link to="/about">Know More</Link>
                </Button>
                <Button asChild variant="outline">
                  <a href={getWhatsAppUrl()} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="h-4 w-4 mr-2" /> Chat with Doctor
                  </a>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Before & After Section */}
      <BeforeAfterCarousel
        eyebrow="SMILE TRANSFORMATIONS"
        heading="Real patients. Real results."
        subheading={`A glimpse of the smiles crafted at ${CLINIC_NAME}.`}
      />
      <div className="text-center -mt-8 md:-mt-12 pb-16">
        <Button asChild variant="outline">
          <Link to="/smile-gallery">View Full Gallery →</Link>
        </Button>
      </div>

      {/* Google Reviews — Scrollable */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center justify-center gap-3 mb-3">
              <GoogleLogo />
              <h2 className="font-heading text-2xl md:text-3xl font-bold">Google Reviews</h2>
            </div>
            <div className="flex items-center justify-center gap-1 mb-1">
              {[1,2,3,4,5].map(i => <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />)}
            </div>
            <p className="text-sm text-muted-foreground">4.9 rated on Google</p>
          </motion.div>

          {/* Text Reviews Carousel */}
          <div className="relative mb-10">
            <button onClick={() => scrollReviews("left")} aria-label="Previous reviews" className="absolute -left-2 md:-left-4 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-card border border-border flex items-center justify-center shadow-md hover:bg-accent transition-colors">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button onClick={() => scrollReviews("right")} aria-label="Next reviews" className="absolute -right-2 md:-right-4 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-card border border-border flex items-center justify-center shadow-md hover:bg-accent transition-colors">
              <ChevronRight className="h-4 w-4" />
            </button>
            <div ref={reviewsRef} className="flex gap-4 overflow-x-auto scrollbar-hide pb-4 snap-x snap-mandatory px-1">
              {googleReviews.map((review, i) => (
                <Card key={i} className="min-w-[280px] max-w-[300px] snap-start shrink-0">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-sm">
                          {review.name.charAt(0)}
                        </div>
                        <div>
                          <p className="font-heading font-semibold text-sm">{review.name}</p>
                          <p className="text-xs text-muted-foreground">{review.time}</p>
                        </div>
                      </div>
                      <GoogleLogo />
                    </div>
                    <div className="flex gap-0.5 mb-3">
                      {Array.from({ length: review.rating }).map((_, j) => (
                        <Star key={j} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">"{review.review}"</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <div className="text-center mt-8">
            <Button asChild variant="outline">
              <a href="https://g.page/r/mutha-advanced-dental-care/review" target="_blank" rel="noopener noreferrer">
                Write a Google Review →
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <CTABanner />

      {/* Instagram Section */}
      <InstagramEmbeds />

      {/* FAQ */}
      <section className="py-16 md:py-20">
        <motion.div
          className="container max-w-2xl"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="font-heading text-2xl md:text-3xl font-bold mb-8 text-center">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="text-left text-sm">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </section>

      {/* Location Strip */}
      <section className="py-12 bg-accent/30 border-t border-border">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="font-heading text-xl font-bold mb-3">Visit Us</h2>
              <div className="space-y-2 text-sm text-muted-foreground">
                <a href={GOOGLE_MAPS_DIRECTIONS} target="_blank" rel="noopener noreferrer" onClick={() => trackDirections("home_visit_us")} className="flex items-center gap-2 text-primary hover:underline">
                  <MapPin className="h-4 w-4" /> {CLINIC_ADDRESS} — Get directions
                </a>
                <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> Mon-Sat: {CLINIC_TIMINGS.weekdays}</p>
                <a href={PHONE_PRIMARY_TEL} onClick={() => trackCall("home_visit_us")} className="flex items-center gap-2 hover:text-primary"><Phone className="h-4 w-4 text-primary" /> {PHONE_PRIMARY}</a>
              </div>
            </motion.div>
            <motion.div
              className="min-w-0 rounded-xl overflow-hidden h-48 bg-muted border border-border"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <iframe
                src={GOOGLE_MAPS_EMBED}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Mutha's Advanced Dental Care Location"
              />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Index;
